import { ChevronLeft, ChevronRight } from "lucide-react"
import { SelectMenu } from "@/app/components/SelectMenu"
import type { ClientTheme } from "@/app/lib/themes"
import { PER_PAGE_OPTIONS, type PerPage } from "@/app/pages/TransactionsPage/useTransactionsView"

interface PaginationControlsProps {
  page: number
  totalPages: number
  perPage: PerPage
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: PerPage) => void
  /** While a background refetch is in flight (e.g. keepPreviousData is
   *  showing the prior page) — Prev/Next are disabled to avoid a double-jump. */
  disabled?: boolean
  theme: ClientTheme
}

/**
 * Prev/Next + "Page X of Y" + per-page select. Rendered inside a table
 * footer bar (which already flex-wraps), so it stays usable on mobile without
 * its own breakpoint handling.
 */
export function PaginationControls({
  page,
  totalPages,
  perPage,
  onPageChange,
  onPerPageChange,
  disabled,
  theme,
}: PaginationControlsProps) {
  const clampedTotalPages = Math.max(totalPages, 1)
  const canPrev = !disabled && page > 1
  const canNext = !disabled && page < clampedTotalPages

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 flex-shrink-0">
        <span style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textMuted }}>
          Rows per page
        </span>
        <SelectMenu
          value={String(perPage)}
          onChange={(v) => onPerPageChange(Number(v) as PerPage)}
          options={PER_PAGE_OPTIONS.map((n) => ({ label: String(n), value: String(n) }))}
          width="76px"
          background={theme.bgSurface2}
          theme={theme}
        />
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={!canPrev}
          aria-label="Previous page"
          className="flex items-center justify-center rounded-lg"
          style={navButton(theme, canPrev)}
        >
          <ChevronLeft size={14} />
        </button>
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "12px",
            color: theme.textMuted,
            whiteSpace: "nowrap",
          }}
        >
          Page <span style={{ fontWeight: 600, color: theme.textPrimary }}>{page}</span> of{" "}
          {clampedTotalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={!canNext}
          aria-label="Next page"
          className="flex items-center justify-center rounded-lg"
          style={navButton(theme, canNext)}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  )
}

function navButton(theme: ClientTheme, enabled: boolean): React.CSSProperties {
  return {
    width: "28px",
    height: "28px",
    backgroundColor: theme.bgSurface,
    color: enabled ? theme.textPrimary : theme.textDim,
    border: `1px solid ${theme.borderColor}`,
    opacity: enabled ? 1 : 0.5,
    cursor: enabled ? "pointer" : "default",
  }
}
